import React from 'react';
import { Typography, Flex } from 'antd';
import { Video } from '@monorepo/types';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { getStatusColor } from '../../series/series-videos/series-videos';
import SocialIcon from '../../../components/social-provider-icon/social-provider-icon';
import { SocialProvider } from '@monorepo/types';
import { getVideoDisplayDate } from '../utils/date-utils';
import { getStatusColors } from '../utils/color-utils';

import './daily-timeline-view.scss';

interface DailyTimelineViewProps {
  selectedDate: Dayjs;
  videos: Video[];
}

const DailyTimelineView: React.FC<DailyTimelineViewProps> = ({
  selectedDate,
  videos,
}) => {
  const navigate = useNavigate();

  // Generate hours (6 AM to 11 PM)
  const hours: number[] = [];
  for (let h = 6; h <= 23; h++) {
    hours.push(h);
  }

  // Get videos for a specific hour
  const getVideosForHour = (hour: number): Video[] => {
    return videos.filter((video) => {
      const videoDate = dayjs(getVideoDisplayDate(video));
      return videoDate.isSame(selectedDate, 'day') && videoDate.hour() === hour;
    });
  };

  const formatHour = (hour: number): string => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
  };

  return (
    <div className="daily-timeline-view">
      <Typography.Title level={4} style={{ marginBottom: '16px' }}>
        {selectedDate.format('dddd, MMMM D, YYYY')}
      </Typography.Title>

      <div className="daily-timeline-container">
        {hours.map((hour) => {
          const hourVideos = getVideosForHour(hour);

          return (
            <div key={hour} className="daily-timeline-row">
              <div className="daily-timeline-hour-label">
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: '12px', fontWeight: 500 }}
                >
                  {formatHour(hour)}
                </Typography.Text>
              </div>
              <div className="daily-timeline-content">
                {hourVideos.length === 0 ? (
                  <div className="daily-timeline-empty">
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: '11px' }}
                    >
                      No videos
                    </Typography.Text>
                  </div>
                ) : (
                  <div className="daily-timeline-videos">
                    {hourVideos.map((video) => {
                      const { bgColor, borderColor } = getStatusColors(
                        video.status
                      );

                      return (
                        <div
                          key={video._id}
                          className="daily-timeline-video-card"
                          onClick={() => navigate(`/videos/${video._id}`)}
                          style={{
                            background: bgColor,
                            borderLeft: `3px solid ${borderColor}`,
                          }}
                        >
                          <Flex
                            justify="space-between"
                            align="center"
                            style={{ marginBottom: '4px' }}
                          >
                            <Typography.Text
                              style={{
                                fontSize: '11px',
                                color: '#595959',
                                fontWeight: 500,
                              }}
                            >
                              {dayjs(getVideoDisplayDate(video)).format(
                                'h:mm A'
                              )}
                            </Typography.Text>
                            {video.publishedAccounts &&
                              video.publishedAccounts.length > 0 && (
                                <SocialIcon
                                  provider={
                                    video.publishedAccounts[0]
                                      .socialPlatform as SocialProvider
                                  }
                                  style={{ fontSize: '14px' }}
                                />
                              )}
                          </Flex>
                          <Typography.Text
                            strong
                            style={{
                              fontSize: '13px',
                              lineHeight: 1.4,
                            }}
                          >
                            {video.title}
                          </Typography.Text>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DailyTimelineView;
